import React, { useState, useRef } from "react";
import Typography from "apollo-react/components/Typography";
import Box from "apollo-react/components/Box";
import Button from "apollo-react/components/Button";
import IconButton from "apollo-react/components/IconButton";
import TextField from "apollo-react/components/TextField";
import Modal from "apollo-react/components/Modal";
import Download from "apollo-react-icons/Download";
import EyeShow2 from 'apollo-react-icons/EyeShow2';
import Attachment from "apollo-react-icons/Paperclip";
import Pencil from 'apollo-react-icons/Pencil';
import Trash from "apollo-react-icons/Trash";
import Close from "apollo-react-icons/Close";
import Plus from "apollo-react-icons/Plus";
import { useFileDownload, useFilePreview, useFileUpload } from "../../../hooks";

const CommentViewer = ({
  comments = [],
  persona = "customer",
  commentIndex = 0,
  onEdit,
  onDelete,
}) => {
  const { downloadFile, downloading } = useFileDownload(persona);
  const { canPreviewFile } = useFilePreview(persona);
  const { uploadFile, uploading } = useFileUpload(persona !== "customer");
  const fileInputRef = useRef(null);

  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedText, setEditedText] = useState("");
  // attachment editing state
  const [removeAttachment, setRemoveAttachment] = useState(false);
  const [newAttachment, setNewAttachment] = useState(null);
  const [newAttachmentData, setNewAttachmentData] = useState(null);
  // delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  const handleDownload = async (fileId, fileName) => {
    await downloadFile(fileId, fileName || `attachment_${fileId}`);
  };

  const handlePreview = (fileId, fileName) => {
    try {
      // determine endpoint based on persona
      const endpoint =
        persona === "customer"
          ? `/api/v1/files/preview/${fileId}`
          : `/api/v1/files/admin/preview/${fileId}`;

      // create the full url using react_app_server_url
      const fullUrl = `${process.env.REACT_APP_SERVER_URL}${endpoint}`;

      // create a temporary anchor element and click it programmatically
      // this method is less likely to be blocked by popup blockers
      const link = document.createElement("a");
      link.href = fullUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("error opening file preview:", error);
      alert(`could not open preview for ${fileName || `file ${fileId}`}`);
    }
  };

  const handleEditClick = (comment) => {
    setEditingCommentId(comment.id);
    setEditedText(comment.comment);
    setRemoveAttachment(false);
    setNewAttachment(null);
    setNewAttachmentData(null);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditedText("");
    setRemoveAttachment(false);
    setNewAttachment(null);
    setNewAttachmentData(null);
  };

  const handleRemoveAttachment = () => {
    setRemoveAttachment(true);
    setNewAttachment(null);
    setNewAttachmentData(null);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // upload the file immediately
      const uploadedFile = await uploadFile(file);
      setNewAttachment(file);
      setNewAttachmentData(uploadedFile);
      setRemoveAttachment(false); // we're replacing, not just removing
    } catch (error) {
      console.error("error uploading file:", error);
      alert("failed to upload file");
    }

    // reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSaveEdit = async (commentId, originalAttachmentId) => {
    if (!onEdit) return;
    try {
      // build attachment info object
      const attachmentInfo = {
        remove_attachment: removeAttachment && !newAttachmentData,
        new_file_id: newAttachmentData?.id || null,
        new_file_data: newAttachmentData || null,
      };

      await onEdit(commentId, editedText, attachmentInfo);
      setEditingCommentId(null);
      setEditedText("");
      setRemoveAttachment(false);
      setNewAttachment(null);
      setNewAttachmentData(null);
    } catch (error) {
      console.error("error saving comment:", error);
    }
  };

  const handleDeleteClick = (commentId) => {
    if (!onDelete) return;
    setCommentToDelete(commentId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!commentToDelete) return;
    try {
      await onDelete(commentToDelete);
    } catch (error) {
      console.error("error deleting comment:", error);
    } finally {
      setShowDeleteModal(false);
      setCommentToDelete(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setCommentToDelete(null);
  };

  return (
    <Box sx={{ marginBottom: 3 }}>
      {comments.map((comment, index) => {
        const attachedFileId = comment.additional_file_id;
        const attachedFile = comment.additional_file;
        const isEditing = editingCommentId === comment.id;

        // get filename from the embedded file data
        const fileName =
          attachedFile?.original_name ||
          attachedFile?.name ||
          `attachment_${attachedFileId}`;
        const mimeType = attachedFile?.mime_type || "";
        const isPreviewable = attachedFileId && canPreviewFile(mimeType);



        return (
          <Box
            key={index}
            sx={{
              paddingBottom: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                marginBottom: 1,
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "baseline" }}>
              <Typography
                sx={{
                  fontFamily: "Proxima Nova",
                  fontWeight: 700,
                    fontStyle: "bold",
                  fontSize: "17px",
                  lineHeight: "20px",
                  letterSpacing: 0,
                }}
              >
                {comment?.user?.username || comment?.end_user?.username}
              </Typography>
                <Typography sx={{ fontFamily: "Proxima Nova", fontStyle: "regular", fontWeight: 400, fontSize: "15px", lineHeight: "20px", color: "#666", letterSpacing: 0 }}>
                  {comment?.user?.Role?.name || comment?.end_user?.end_users_category?.name}
                </Typography>
              </Box>
              <Typography sx={{ color: "#999", fontWeight: 500, fontSize: "12px", lineHeight: "18px", letterSpacing: 0 }}>
                {new Date(comment.createdAt).toLocaleDateString('en-GB')} -{" "}
                {new Date(comment.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>

            </Box>
            {(comment.user?.email || comment?.end_user?.email) && (
              <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "fit-content",
                marginBottom: 1.5,
              }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#000",
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "140%",
                    letterSpacing: 0,
                  }}
                >
                  {comment.user?.email || comment?.end_user?.email}
                </Typography>
              </Box>
            )}
            {/* ectd version label */}
            {comment.ectd_version && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  backgroundColor: "#E3F2FD",
                  border: "1px solid #90CAF9",
                  marginBottom: 2,
                }}
              >
                <Typography
                  variant="bodySmall"
                  sx={{
                    color: "#1976D2",
                    fontWeight: 600,
                    fontSize: "12px",
                    lineHeight: "18px",
                  }}
                >
                  Version {comment.ectd_version.version_number}
                  {comment.ectd_version.sequence_number && ` (${comment.ectd_version.sequence_number})`}
                </Typography>
              </Box>
            )}
            {isEditing ? (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  sx={{ marginBottom: 1 }}
                />

                {/* attachment editing section */}
                <Box sx={{ marginBottom: 2 }}>
                  {/* show current attachment with remove option */}
                  {attachedFileId && !removeAttachment && !newAttachmentData && (
                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      padding: 1,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 1,
                      marginBottom: 1
                    }}>
                      <Attachment color="#666" style={{ width: "16px", height: "16px" }} />
                      <Typography variant="bodySmall" sx={{ flexGrow: 1, color: "#555" }}>
                        {fileName}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleRemoveAttachment}
                        sx={{ padding: "2px" }}
                        title="Remove Attachment"
                      >
                        <Close size={14} color="#d32f2f" />
                      </IconButton>
                    </Box>
                  )}

                  {/* show new attachment info */}
                  {newAttachmentData && (
                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      padding: 1,
                      backgroundColor: "#e8f5e9",
                      borderRadius: 1,
                      marginBottom: 1
                    }}>
                      <Attachment color="#2e7d32" style={{ width: "16px", height: "16px" }} />
                      <Typography variant="bodySmall" sx={{ flexGrow: 1, color: "#2e7d32" }}>
                        {newAttachment?.name || newAttachmentData.original_name} (new)
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setNewAttachment(null);
                          setNewAttachmentData(null);
                        }}
                        sx={{ padding: "2px" }}
                        title="Remove New Attachment"
                      >
                        <Close size={14} color="#d32f2f" />
                      </IconButton>
                    </Box>
                  )}

                  {/* show "attachment removed" message */}
                  {removeAttachment && !newAttachmentData && attachedFileId && (
                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      padding: 1,
                      backgroundColor: "#ffebee",
                      borderRadius: 1,
                      marginBottom: 1
                    }}>
                      <Typography variant="bodySmall" sx={{ color: "#d32f2f" }}>
                        Attachment will be Removed
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setRemoveAttachment(false)}
                        sx={{ fontSize: "12px", padding: "2px 8px" }}
                      >
                        Undo
                      </Button>
                    </Box>
                  )}

                  {/* add attachment button */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                  />
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<Plus size={14} />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    sx={{ fontSize: "12px" }}
                  >
                    {uploading ? "Uploading..." : (attachedFileId && !removeAttachment && !newAttachmentData) ? "Replace Attachment" : "Add Attachment"}
                  </Button>
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleSaveEdit(comment.id, attachedFileId)}
                    disabled={uploading}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
                <Box sx={{ backgroundColor: "#F5F5F5", padding: 2, borderRadius: 1 }}>
                  <Typography
                    variant="bodyDefault"
                    sx={{
                      fontFamily: "Proxima Nova",
                      fontWeight: 400,
                      fontStyle: "regular",
                      fontSize: "14px",
                      lineHeight: "20px",
                      letterSpacing: 0,
                      color: "#000000",
                    }}
                  >
                    {comment.comment}
                  </Typography>
                  {/* edit/delete buttons inside comment box */}
                  {comment.isOwner && onEdit && onDelete && !isEditing && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, marginTop: 2 }}>
                      <Trash
                        style={{ width: "14px", height: "14px", cursor: "pointer", color: "#0557D5" }}
                        onClick={() => handleDeleteClick(comment.id)}
                        title="Delete Comment"
                      />
                      <Box sx={{ width: "1px", height: "10px", backgroundColor: "#0557D5" }} />
                      <Pencil
                        style={{ width: "14px", height: "14px", cursor: "pointer", color: "#0557D5" }}
                        onClick={() => handleEditClick(comment)}
                        title="Edit Comment"
                      />
                    </Box>
                )}
              </Box>
            )}
            {/* file attachment */}
            {attachedFileId && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  marginTop: 2,
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", height: "29px" }}>
                  <Attachment color="#666" style={{ width: "18px", height: "18px" }} />
                </Box>
                <Typography
                  variant="bodySmall"
                  sx={{
                    color: "#555",
                    flexGrow: 1,
                    fontWeight: 500,
                    marginRight: 1,
                  }}
                >
                  {fileName}
                </Typography>

                <Box sx={{ display: "flex", gap: 0.25, alignItems: "center" }}>
                  {isPreviewable && (

                      <Button
                      variant="secondary"
                        size="small"
                        endIcon={<EyeShow2 size={11} />}
                        onClick={() => handlePreview(attachedFileId, fileName)}
                        sx={{
                          minWidth: "auto",
                          minHeight: "20px",
                          width: "150px",
                          height: "29px",
                          fontSize: "14px",
                          padding: "4px 22.5px",
                          gap: "4px",
                          '& .MuiButton-endIcon': {
                            marginLeft: "2px !important",
                          },
                        }}
                      >
                      <span style={{ fontWeight: 400 }}>Preview</span>
                    </Button>
                  )}

                    <Button
                    variant="secondary"
                      size="small"
                      endIcon={<Download size={11} />}
                      onClick={() => handleDownload(attachedFileId, fileName)}
                      disabled={downloading}
                      sx={{
                        minWidth: "auto",
                        width: "150px",
                        height: "29px",
                        fontSize: "14px",
                        marginLeft: "8px",
                        padding: "4px 22.5px",
                        backgroundColor: "transparent !important",
                        gap: "4px",
                        '& .MuiButton-endIcon': {
                          marginLeft: "2px !important",
                        }
                      }}
                    >
                    <span style={{ fontWeight: 400 }}>
                        {downloading ? "Downloading..." : "Download"}
                      </span>
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        );
      })}

      {/* delete confirmation modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        title="Delete Comment"
        message="Are you sure you want to delete this comment?"
        buttonProps={[
          {
            label: "Cancel",
            onClick: handleCloseDeleteModal,
            variant: "secondary",
          },
          {
            label: "Delete",
            onClick: handleConfirmDelete,
          },
        ]}
      />
    </Box>
  );
};

export default CommentViewer;
